import React, { useState, useEffect } from 'react';
import { getMyBewertungen, getTermineByCustomer } from '../api';
import TerminDetailDialog from '../components/termine/TerminDetailDialog';

function MyBewertungen({ user, onBack }) {
  const [bewertungen, setBewertungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedTermin, setSelectedTermin] = useState(null);
  const [showTerminDialog, setShowTerminDialog] = useState(false);
  const [terminLoading, setTerminLoading] = useState(false);

  // Mobile Responsive Check
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Daten laden
  useEffect(() => {
    loadBewertungen();
  }, []);

  const loadBewertungen = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getMyBewertungen(user.id, user.isFachberater);
      if (response.success) {
        console.log('Bewertungen geladen:', response.bewertungen);
        setBewertungen(response.bewertungen || []);
      } else {
        setError('Fehler beim Laden der Bewertungen: ' + response.error);
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Neu': '#3182ce',
      'Qualifiziert': '#38a169',
      'Kein Interesse': '#e53e3e',
      'Widerrufen': '#d69e2e',
      'Nicht qualifiziert': '#718096'
    };
    return statusColors[status] || '#718096';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleNameClick = async (bewertung) => {
    console.log('Bewertung geklickt:', bewertung);
    
    // Verwende entweder lead_id oder id aus der leads Tabelle
    const customerId = bewertung.lead_id || bewertung.id;
    
    console.log('Verwende Customer ID:', customerId);
    
    if (!customerId) {
      alert('Keine Lead-ID verfügbar für diesen Eintrag.');
      return;
    }

    setTerminLoading(true);
    try {
      // Lade Termine für diesen Kunden
      const response = await getTermineByCustomer(user.id, customerId);
      
      if (response.success && response.termine && response.termine.length > 0) {
        // Nehme den ersten Termin (oder den neuesten)
        const termin = response.termine[0];
        setSelectedTermin(termin);
        setShowTerminDialog(true);
      } else {
        alert('Keine Termine für diesen Kunden gefunden.');
      }
    } catch (err) {
      alert('Fehler beim Laden der Termin-Details: ' + err.message);
    } finally {
      setTerminLoading(false);
    }
  };

  const closeTerminDialog = () => {
    setShowTerminDialog(false);
    setSelectedTermin(null);
  };

  // Responsive Styles
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '10px' : '20px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '20px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.4rem' : '1.8rem',
      margin: 0,
      fontWeight: '600',
    },
    backButton: {
      padding: '8px 16px',
      background: '#4a5568',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    tableHeader: {
      background: '#f7fafc',
      borderBottom: '2px solid #e2e8f0',
    },
    tableHeaderCell: {
      padding: isMobile ? '8px 4px' : '12px 16px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#4a5568',
      fontSize: isMobile ? '12px' : '14px',
    },
    tableRow: {
      borderBottom: '1px solid #e2e8f0',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f7fafc',
    },
    tableCell: {
      padding: isMobile ? '8px 4px' : '12px 16px',
      fontSize: isMobile ? '12px' : '14px',
      color: '#2d3748',
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      color: 'white',
      display: 'inline-block',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#4a5568',
      fontSize: '16px',
    },
    error: {
      textAlign: 'center',
      padding: '20px',
      color: '#e53e3e',
      background: '#fed7d7',
      borderRadius: '6px',
      margin: '20px 0',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#718096',
      fontSize: '16px',
    },
    mobileCard: {
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
    },
    mobileCardTitle: {
      fontWeight: '600',
      marginBottom: '8px',
      color: '#2d3748',
    },
    mobileCardRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '6px',
      fontSize: '14px',
    },
    mobileCardLabel: {
      color: '#718096',
      fontWeight: '500',
    },
    mobileCardValue: {
      color: '#2d3748',
    },
    clickableName: {
      color: '#3182ce',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    loadingIndicator: {
      marginLeft: '5px',
      fontSize: '0.8em',
      color: '#4a5568',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>
            ← Zurück
          </button>
          <h1 style={styles.title}>Alle Bewertungen</h1>
        </div>
        <div style={styles.loading}>Bewertungen werden geladen...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>
            ← Zurück
          </button>
          <h1 style={styles.title}>Alle Bewertungen</h1>
        </div>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (bewertungen.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>
            ← Zurück
          </button>
          <h1 style={styles.title}>Alle Bewertungen</h1>
        </div>
        <div style={styles.emptyState}>
          Keine Bewertungen gefunden.
        </div>
      </div>
    );
  }

  // Mobile Ansicht
  if (isMobile) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={onBack}>
            ← Zurück
          </button>
          <h1 style={styles.title}>Alle Bewertungen</h1>
        </div>
        
        <div>
          {bewertungen.map((bewertung, index) => (
            <div key={index} style={styles.mobileCard}>
              <div 
                style={{
                  ...styles.mobileCardTitle,
                  ...styles.clickableName,
                  cursor: terminLoading ? 'wait' : 'pointer'
                }}
                onClick={() => !terminLoading && handleNameClick(bewertung)}
                title="Klicken für Termin-Details"
              >
                {bewertung.name}
                {terminLoading && <span style={styles.loadingIndicator}>...</span>}
              </div>
              
              <div style={styles.mobileCardRow}>
                <span style={styles.mobileCardLabel}>Status:</span>
                <span>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(bewertung.status)
                  }}>
                    {bewertung.status || 'Nicht gesetzt'}
                  </span>
                </span>
              </div>
              
              <div style={styles.mobileCardRow}>
                <span style={styles.mobileCardLabel}>Bewertet am:</span>
                <span style={styles.mobileCardValue}>
                  {formatDate(bewertung.bewertet_am)}
                </span>
              </div>
              
              <div style={styles.mobileCardRow}>
                <span style={styles.mobileCardLabel}>Notiz:</span>
                <span style={styles.mobileCardValue}>
                  {truncateText(bewertung.notiz, 30)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Termin Detail Dialog */}
        {showTerminDialog && selectedTermin && (
          <TerminDetailDialog
            termin={selectedTermin}
            user={user}
            onClose={closeTerminDialog}
          />
        )}
      </div>
    );
  }

  // Desktop Ansicht
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={onBack}>
          ← Zurück
        </button>
        <h1 style={styles.title}>Alle Bewertungen</h1>
      </div>
      
      <table style={styles.table}>
        <thead style={styles.tableHeader}>
          <tr>
            <th style={styles.tableHeaderCell}>Name</th>
            <th style={styles.tableHeaderCell}>Status</th>
            <th style={styles.tableHeaderCell}>Bewertet am</th>
            <th style={styles.tableHeaderCell}>Notiz</th>
          </tr>
        </thead>
        <tbody>
          {bewertungen.map((bewertung, index) => (
            <tr key={index} style={styles.tableRow}>
              <td style={styles.tableCell}>
                <span 
                  style={{
                    ...styles.clickableName,
                    cursor: terminLoading ? 'wait' : 'pointer'
                  }}
                  onClick={() => !terminLoading && handleNameClick(bewertung)}
                  title="Klicken für Termin-Details"
                >
                  {bewertung.name}
                  {terminLoading && <span style={styles.loadingIndicator}>...</span>}
                </span>
              </td>
              <td style={styles.tableCell}>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: getStatusColor(bewertung.status)
                }}>
                  {bewertung.status || 'Nicht gesetzt'}
                </span>
              </td>
              <td style={styles.tableCell}>
                {formatDate(bewertung.bewertet_am)}
              </td>
              <td style={styles.tableCell}>
                {truncateText(bewertung.notiz)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Termin Detail Dialog */}
      {showTerminDialog && selectedTermin && (
        <TerminDetailDialog
          termin={selectedTermin}
          user={user}
          onClose={closeTerminDialog}
        />
      )}
    </div>
  );
}

export default MyBewertungen; 